import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, ArrowDownIcon, GlobeIcon } from 'lucide-react';
import { Countdown } from './Countdown';
export function HeroSection() {
  return <section className="relative min-h-[85vh] md:min-h-screen flex items-center pt-20 pb-10 md:pt-24 md:pb-16 overflow-hidden" data-id="element-244">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.15)_0%,transparent_60%)]" data-id="element-245"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-246">
        <div className="text-center max-w-4xl mx-auto" data-id="element-247">
          {/* Trending Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 md:py-2.5 rounded-full glass-card border-brand-cyan/50 shadow-[0_0_20px_rgba(0,102,255,0.3)] mb-5 md:mb-8" data-id="element-248">
            <span className="relative flex h-3 w-3" data-id="element-249">
              <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-cyan" data-id="element-250"></span>
            </span>
            <span className="text-sm font-bold text-white tracking-wide" data-id="element-251">
              #1 TRENDING — WORLD CUP 2026
            </span>
          </motion.div>

          {/* Tagline */}
          <motion.p initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="text-sm sm:text-xl md:text-2xl text-brand-glow mb-3 md:mb-5 tracking-widest uppercase font-bold electric-glow" data-id="element-252">
            Don't Just Watch History — Wear It
          </motion.p>

          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl sm:text-5xl md:text-7xl font-heading font-black tracking-tight mb-6 md:mb-8 leading-tight" data-id="element-253">
            Tap Your Shirt. <br className="hidden sm:block" data-id="element-254" />
            <span className="text-gradient drop-shadow-[0_0_40px_rgba(0,102,255,0.5)]" data-id="element-255">
              Unlock Exclusive Content.
            </span>
          </motion.h1>

          {/* Hero Video Demo */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-8 md:mb-12 max-w-3xl mx-auto" data-id="element-256">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-brand-dark border border-white/10 shadow-[0_0_40px_rgba(0,102,255,0.2)]" data-id="element-257">
              <video controls playsInline preload="metadata" className="w-full h-full object-cover" data-id="element-258">
                <source src="https://cdn.magicpatterns.com/uploads/oQ9SPk98zGJCqAQTd7RMpB/0423%20(1)%20(1).mp4#t=0.001" type="video/mp4" data-id="element-259" />
              </video>
            </div>
          </motion.div>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-base md:text-xl text-gray-300 mb-6 md:mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-260">
            The world's first NFC smart t-shirt for the World Cup. Anyone can
            tap your shirt with their phone to instantly see your team profile,
            live match stats, social links, and fan content —{' '}
            <span className="font-bold text-white" data-id="element-261">no app needed.</span>{' '}
            <span className="font-bold text-white uppercase" data-id="element-262">
              100% WATERPROOF.
            </span>{' '}
            Updated in real time.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-6 md:mb-10" data-id="element-263">
            <a href="#pricing" className="w-full sm:w-auto bg-brand-cyan hover:bg-brand-glow text-white font-bold text-lg py-4 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_35px_rgba(0,102,255,0.8)] flex items-center justify-center gap-2 hover:-translate-y-1" data-id="element-264">
              <ZapIcon className="w-5 h-5 fill-current" data-id="element-265" />
              Order Now — Limited Drop
            </a>
            <a href="#nfc-animation" className="w-full sm:w-auto glass-card hover:bg-white/10 text-white font-medium text-lg py-4 px-8 rounded-full transition-all flex items-center justify-center gap-2" data-id="element-266">
              See How It Works <ArrowDownIcon className="w-5 h-5" data-id="element-267" />
            </a>
          </motion.div>

          {/* Flash Bundle Promo */}
          <motion.a href="#pricing" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-12 md:mb-20 cursor-pointer group transition-all hover:-translate-y-0.5" style={{
          background: 'linear-gradient(135deg, rgba(250,204,21,0.1) 0%, rgba(0,102,255,0.1) 100%)',
          border: '1px solid rgba(250,204,21,0.25)'
        }} data-id="element-268">
            <span className="text-yellow-400 text-sm" data-id="element-269">🔥</span>
            <span className="text-sm font-bold text-yellow-400 group-hover:text-yellow-300 transition-colors" data-id="element-270">
              Flash Bundles — Save up to 25%
            </span>
            <span className="text-yellow-400/60 text-xs" data-id="element-271">→</span>
          </motion.a>

          {/* World Cup Event Section */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="relative" data-id="element-272">
            <div className="absolute inset-0 bg-brand-cyan/10 blur-[100px] rounded-full" data-id="element-273"></div>

            <div className="flex items-center justify-center gap-1.5 md:gap-2 mb-4 md:mb-6" data-id="element-274">
              <GlobeIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-glow" data-id="element-275" />
              <p className="text-[10px] md:text-sm font-bold text-brand-glow uppercase tracking-widest" data-id="element-276">
                FIFA World Cup 2026 — June 11, Mexico City
              </p>
              <GlobeIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-glow" data-id="element-277" />
            </div>

            <Countdown data-id="element-278" />

            {/* Social Proof */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1,
            duration: 1
          }} className="mt-4 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm font-medium text-gray-300" data-id="element-279">
              <p className="flex items-center gap-2" data-id="element-280">
                <span className="relative flex h-2 w-2" data-id="element-281">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-282"></span>
                </span>
                <strong className="text-white" data-id="element-283">47,000+ fans</strong> already
                secured theirs
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}